<script setup>
import { ref, computed, onMounted } from 'vue'
import UsersHeader from '@/pages/admin/AdminHeader.vue';
import { useLayoutComponents } from '@/composables/layout.js';
import Modal from '@/components/modals/Modal.vue';
import { getAcceptedFileFormats, createAcceptedFileFormat, deleteAcceptedFileFormat } from '@/services/fileService.js';
import { useToast } from '@/composables/use-toast.js';

const { SelectInput, TextInput, InputError, PrimaryButton, ButtonLoading } = useLayoutComponents();
const { addToast } = useToast();

const itemsPerPageOptions = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
];

const categoryOptions = [
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' },
    { value: 'audio', label: 'Audio' },
    { value: 'document', label: 'Document' },
    { value: 'code', label: 'Code' },
    { value: 'text', label: 'Text' },
    { value: 'data', label: 'Data' },
];

const pattern = '^[a-z0-9_-]{1,13}$';

const formats = ref([]);
const searchQuery = ref('');
const categorySelected = ref(null);
const currentPage = ref(1);
const itemsPerPage = ref(10);

const showModal = ref(false);
const showDeleteModal = ref(false);
const isLoading = ref(false);
const loadingText = ref('Adding ...');
const formatToDelete = ref(null);
const requestErrorMessage = ref(null);

const newFormat = ref({
    extension: '',
    category: null,
});

const filteredFormats = computed(() => {
    let filtered = formats.value;
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = formats.value.filter(format => 
            format.extension.toLowerCase().includes(query) ||
            format.category.toLowerCase().includes(query)
        );
    }
    if (categorySelected.value) {
        filtered = filtered.filter(format => format.category === categorySelected.value);
    }
    return filtered;
});

const totalPages = computed(() => {
    return Math.ceil(filteredFormats.value.length / itemsPerPage.value);
});

const paginatedFormats = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredFormats.value.slice(start, end);
});

const isExtensionValid = computed(() => {
    return newFormat.value.extension !== '' ? new RegExp(pattern).test(newFormat.value.extension) : true;
});

const categoryBadge = (category) => {
    return {
        'bg-teal-800': category === 'image',
        'bg-cyan-800': category === 'video',
        'bg-sky-800': category === 'audio',
        'bg-blue-800': category === 'document',
        'bg-indigo-800': category === 'code',
        'bg-violet-800': category === 'text',
        'bg-purple-800': category === 'data',
        'bg-gray-800': category === 'other',
    }
}

const categorySelectBadge = (category) => {
    return categorySelected.value === null || categorySelected.value === category ? categoryBadge(category) : 'bg-white/20';
}

const setAcceptedFileFormats = async () => {
    const response = await getAcceptedFileFormats();
    if(response.data && Array.isArray(response.data)) {
        formats.value = response.data;
    } else {
        console.error('Error fetching accepted file formats:', response.data);
    }
}

const handleItemsPerPageChange = () => {
    currentPage.value = 1;
}

const handleCategorySelected = (category) => {
    categorySelected.value = categorySelected.value !== category ? category : null;
}

const handleShowDeleteModal = (format) => {
    formatToDelete.value = format;
    showDeleteModal.value = true;
}

const handleCloseModals = () => {
    showModal.value = false
    showDeleteModal.value = false
    requestErrorMessage.value = null
    newFormat.value = {
        extension: '',
        category: null,
    };

    formatToDelete.value = null;
}

const handleAddFormat = async () => {
    isLoading.value = true;
    const response = await createAcceptedFileFormat(newFormat.value);
    if(response.error) {
        requestErrorMessage.value = response.message;
        isLoading.value = false;
        setTimeout(() => {
            requestErrorMessage.value = null;
        }, 5000);
        return;
    }
    if(response.data && response.data.id) {
        await setAcceptedFileFormats();
        showModal.value = false;
        newFormat.value = {
            extension: '',
            category: null,
        };
        addToast('Format added successfully', 'success');
    }
    isLoading.value = false;
}

const handleDeleteFormat = async () => {
    loadingText.value = 'Deleting ...';
    isLoading.value = true;
    const response = await deleteAcceptedFileFormat(formatToDelete.value.id);
    if(response.status === 200 || response.status === 201) {
        await setAcceptedFileFormats();
        addToast('Format deleted successfully', 'success');
    } else {
        console.error('Error deleting accepted file format:', response.data);
        addToast('Error deleting format', 'error');
    }
    showDeleteModal.value = false;
    isLoading.value = false;
    formatToDelete.value = null;
}

onMounted(async () => {
    await setAcceptedFileFormats();
});
</script>

    <template>
        <main class="p-5 bg-echoes-background-black">
        <UsersHeader>
            <template #title>Files Formats Accept</template>
            <template #description>
                Manage and organize your files formats accept from a central dashboard.
            </template>
            <template #actions>
                <button @click="showModal = true" class="inline-flex items-center px-4 py-2.5 text-base font-medium rounded-xl bg-echoes-primary text-white hover:bg-echoes-primary/80 transition-colors duration-200">
                    <i class="mdi mdi-plus mr-2"></i> Add Format
                </button>
            </template>
        </UsersHeader>

        <div class="py-6">
            <div class="bg-echoes-background-gray shadow-sm rounded-lg border border-echoes-border-light">
                <div class="p-4">
                    <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div class="w-full sm:w-96">
                            <TextInput 
                                v-model="searchQuery" 
                                placeholder="Search formats..." 
                                class="w-full"
                            />
                        </div>
                        <div class="flex items-center gap-4">
                            <div class="w-full sm:w-48">
                                <SelectInput
                                    v-model="itemsPerPage"
                                    :options="itemsPerPageOptions"
                                    :hide-search="true"
                                    @update:modelValue="handleItemsPerPageChange"
                                />
                            </div>
                     
                        </div>
                    </div>
                </div>

                <div class="px-4 pt-2 pb-4 flex gap-4">
                    <div class="text-sm font-semibold text-white/60">Select Category</div>
                    <div class="flex justify-between gap-2 w-1/2">
                        <div v-for="category in categoryOptions" :key="category.value" @click="handleCategorySelected(category.value)" class="category-badge" :class="categorySelectBadge(category.value)">
                            <div>{{ category.label }}</div>
                            <i class="uil uil-times" v-if="categorySelected === category.value"></i>
                        </div>
                    </div>
                </div>
                <div>
                    <div v-if="formats.length === 0" class="h-[40vh]">
                        <div class="flex items-center justify-center h-full">
                            <div class="text-white/80 text-sm">No formats found</div>
                        </div>
                    </div>
                    <table v-else class="min-w-full divide-y divide-echoes-border-light">
                        <thead class="bg-echoes-border-light">
                            <tr>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Extension
                                </th>
                                <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">
                                    Category
                                </th>
                                <th scope="col" class="px-6 py-4 text-end text-xs font-medium text-white uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                            <template v-for="format in paginatedFormats" :key="format.id">
                                <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-200">
                                    <td class="px-6 py-2">
                                        <div class="flex items-center">
                                            <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-semibold bg-echoes-background-gray">
                                                {{ format.extension }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-2 text-center">
                                        <span class="inline-flex items-center justify-center px-3 py-0.5 rounded-full text-sm font-medium" :class="categoryBadge(format.category)">
                                            {{ format.category.slice(0, 1).toUpperCase() + format.category.slice(1) }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-2">
                                        <div class="flex items-center justify-end space-x-4">
                                            <button 
                                                @click="handleShowDeleteModal(format)"
                                                class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light"
                                                title="Delete Format"
                                            >
                                            <i class="mdi mdi-trash-can-outline mr-2"></i>
                                            Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    <!-- Pagination Footer -->
                    <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center text-sm text-white/80">
                                <span>Showing</span>
                                <span class="font-medium mx-1">
                                    {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                                </span>
                                <span>to</span>
                                <span class="font-medium mx-1">
                                    {{ Math.min(currentPage * itemsPerPage, filteredFormats.length) }}
                                </span>
                                <span>of</span>
                                <span class="font-medium ml-1">{{ filteredFormats.length }}</span>
                            </div>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="currentPage = Math.max(1, currentPage - 1)"
                                    :disabled="currentPage === 1"
                                    class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                               
                                <div class="flex items-center space-x-1">
                                    <template v-for="(item, index) in visiblePages" :key="index">
                                        <template v-if="typeof item === 'number'">
                                            <button
                                                @click="currentPage = item"
                                                :class="[
                                                    'px-3 py-1 rounded-md text-sm font-medium',
                                                    currentPage === item
                                                        ? 'bg-echoes-primary text-white'
                                                        : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                                ]"
                                            >
                                                {{ item }}
                                            </button>
                                        </template>
                                        <template v-else>
                                            <span class="px-3 py-1 text-sm text-white/60">...</span>
                                        </template>
                                    </template>
                                </div>
                                <button
                                    @click="currentPage = Math.min(totalPages, currentPage + 1)"
                                    :disabled="currentPage === totalPages"
                                    class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>

    <Modal :isOpen="showModal" title="Add File Format" size="sm" @close="handleCloseModals">
        <div class="flex flex-col gap-4">
            <div class="text-sm text-white/60">
                Typé the extension without the dot (e.g. jpg, png, mp4)
            </div>
            <div v-if="requestErrorMessage" class="text-sm text-red-600 dark:text-red-400 bg-red-400/20 px-2 py-1 rounded-lg border border-red-400 flex items-center gap-2">
                <i class="mdi mdi-alert-circle text-red-500"></i>
                <div>{{ requestErrorMessage }}</div>
            </div>
            <div class="flex items-center gap-2 justify-between"> 
                <div>
                    <TextInput 
                        v-model="newFormat.extension" 
                        placeholder="Extension" 
                        :border-class="!isExtensionValid || requestErrorMessage ? 'border-red-500' : 'border-echoes-border-gray'" 
                        :focus-border-class="!isExtensionValid || requestErrorMessage ? 'border-red-500' : 'border-echoes-border-gray'" />
                    <InputError v-if="!isExtensionValid" :message="'Invalid extension'" />
                </div>
                <SelectInput v-model="newFormat.category" :options="categoryOptions" placeholder="Category" />
            </div>
            <div class="flex justify-end">
                <PrimaryButton @click="handleAddFormat" :disabled="!isExtensionValid || !newFormat.category">

                <ButtonLoading v-if="isLoading" :text="loadingText" size="sm" />
                <div v-else>
                    Save
                </div>
                </PrimaryButton>
            </div>
        </div>
    </Modal>
    <Modal :isOpen="showDeleteModal" title="Delete Format" size="sm" @close="handleCloseModals">
        <div class="flex flex-col gap-4">
            <div class="text-sm">Are you sure you want to delete the format <span class="font-medium">{{ formatToDelete?.extension }}</span>?</div>
            <div class="flex justify-between items-center gap-2">
                <button class="btn--secondary" @click="showDeleteModal = false">No, keep it</button>
                <PrimaryButton class="btn--danger" @click="handleDeleteFormat" :disabled="isLoading">
                    <ButtonLoading v-if="isLoading" :text="loadingText" size="sm" />
                    <div v-else>
                        Yes, delete it
                    </div>
                </PrimaryButton>
            </div>
        </div>
    </Modal>
</template>

<style scoped lang="postcss">
.category-badge {
    @apply inline-flex items-center justify-between px-3 py-0.5 rounded-full text-sm font-medium cursor-pointer;
}

.btn--secondary {
    @apply px-1 text-xs underline hover:text-echoes-primary;
}

</style>